import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Drawer, { Field } from "@/components/Drawer";
import { Plus, Trash2, Pencil, UserCog } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const empty = { email: "", password: "", name: "", role: "sales", phone: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [drawer, setDrawer] = useState(null);

  const load = async () => {
    const r = await api.get("/users");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (drawer.mode === "edit") {
        const { email, ...rest } = data;
        const payload = { ...rest };
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${drawer.data.id}`, payload);
      } else {
        await api.post("/users", data);
      }
      toast.success("Saved");
      setDrawer(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        title="Team"
        subtitle="Manage users and their roles."
        actions={
          <button className="btn-primary flex items-center gap-2" data-testid="add-user-btn"
            onClick={() => setDrawer({ mode: "create", data: empty })}>
            <Plus className="w-4 h-4" /> New User
          </button>
        }
      />
      <div className="crm-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Phone</Th><Th>Created</Th><Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50/50" data-testid={`user-row-${r.id}`}>
                <Td className="font-medium text-slate-900">{r.name}</Td>
                <Td>{r.email}</Td>
                <Td><span className="badge bg-[#10B981]/10 text-[#059669] capitalize">{r.role.replace("_", " ")}</span></Td>
                <Td>{r.phone || "—"}</Td>
                <Td className="text-xs text-slate-500">{fmtDate(r.created_at)}</Td>
                <Td className="text-right">
                  <button className="btn-ghost !p-1.5" onClick={() => setDrawer({ mode: "edit", data: { ...empty, ...r, password: "" } })}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  {r.id !== me?.id && (
                    <button className="btn-ghost !p-1.5 text-red-600" onClick={() => remove(r.id)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </Td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500">
                <UserCog className="w-6 h-6 mx-auto mb-2 opacity-50" /> No users yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {drawer && (
        <Drawer open onClose={() => setDrawer(null)} title={drawer.mode === "edit" ? "Edit User" : "New User"} testid="user-drawer"
          footer={
            <>
              <button className="btn-outline" onClick={() => setDrawer(null)}>Cancel</button>
              <button className="btn-primary" data-testid="user-save-btn" onClick={() => save(drawer.data)}>Save</button>
            </>
          }
        >
          <Field label="Name *"><input className="input-base" value={drawer.data.name} data-testid="user-name" onChange={(e) => setDrawer({ ...drawer, data: { ...drawer.data, name: e.target.value } })} /></Field>
          <Field label="Email *"><input className="input-base" type="email" disabled={drawer.mode === "edit"} value={drawer.data.email} data-testid="user-email" onChange={(e) => setDrawer({ ...drawer, data: { ...drawer.data, email: e.target.value } })} /></Field>
          <Field label={drawer.mode === "edit" ? "Password (leave blank to keep)" : "Password *"}>
            <input className="input-base" type="password" value={drawer.data.password} data-testid="user-password" onChange={(e) => setDrawer({ ...drawer, data: { ...drawer.data, password: e.target.value } })} />
          </Field>
          <div className="grid grid-cols-2 gap-x-4">
            <Field label="Role">
              <select className="input-base" value={drawer.data.role} data-testid="user-role" onChange={(e) => setDrawer({ ...drawer, data: { ...drawer.data, role: e.target.value } })}>
                {["admin", "sales", "project_manager", "technician"].map((k) => <option key={k} value={k}>{k.replace("_", " ")}</option>)}
              </select>
            </Field>
            <Field label="Phone"><input className="input-base" value={drawer.data.phone || ""} onChange={(e) => setDrawer({ ...drawer, data: { ...drawer.data, phone: e.target.value } })} /></Field>
          </div>
        </Drawer>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-3 text-left font-medium ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
